import { useState, useRef, useCallback, useEffect } from 'react';

interface UseVoiceDictationReturn {
  isRecording: boolean;
  isTranscribing: boolean;
  error: string | null;
  volumeData: number[];
  startRecording: () => Promise<void>;
  stopAndTranscribe: () => Promise<string>;
  cancelRecording: () => void;
}

export const useVoiceDictation = (): UseVoiceDictationReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [volumeData, setVolumeData] = useState<number[]>([15, 15, 15, 15, 15]);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const resolveStopRef = useRef<((text: string) => void) | null>(null);
  const rejectStopRef = useRef<((error: Error) => void) | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => { });
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setVolumeData([15, 15, 15, 15, 15]);

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const sendAudioForTranscription = useCallback(async (audioBlob: Blob): Promise<string> => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'dictation.webm');

    const API_URL = process.env.REACT_APP_URL_API_MACADAMIA;
    const response = await fetch(
      `${API_URL}/audio-transcription/transcribe`,
      {
        method: 'POST',
        credentials: 'include',
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Erreur de transcription (${response.status})`);
    }

    const data = await response.json();
    return data.text || '';
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Configuration de l'analyseur audio
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 64;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let lastCall = performance.now();

      const updateVolume = (timestamp: number) => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') return;

        if (timestamp - lastCall > 66) {
          analyser.getByteFrequencyData(dataArray);

          const getAvg = (start: number, end: number) => {
            let sum = 0;
            for (let i = start; i < end; i++) sum += dataArray[i];
            return sum / (end - start);
          };

          const newVolumeData = [
            Math.max(15, (getAvg(0, 4) / 255) * 100),
            Math.max(15, (getAvg(4, 8) / 255) * 100),
            Math.max(15, (getAvg(8, 12) / 255) * 100),
            Math.max(15, (getAvg(12, 16) / 255) * 100),
            Math.max(15, (getAvg(16, 20) / 255) * 100),
          ];

          setVolumeData(newVolumeData);
          lastCall = timestamp;
        }

        animationFrameRef.current = requestAnimationFrame(updateVolume);
      };

      animationFrameRef.current = requestAnimationFrame(updateVolume);

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: mimeType });
        cleanup();

        setIsTranscribing(true);
        try {
          const text = await sendAudioForTranscription(audioBlob);
          setIsTranscribing(false);
          resolveStopRef.current?.(text);
        } catch (err) {
          setIsTranscribing(false);
          const errorMessage = err instanceof Error ? err.message : 'Erreur de transcription';
          setError(errorMessage);
          rejectStopRef.current?.(err instanceof Error ? err : new Error(errorMessage));
        }
        resolveStopRef.current = null;
        rejectStopRef.current = null;
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      cleanup();
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Accès au microphone refusé');
      } else {
        setError('Impossible d\'accéder au microphone');
      }
      throw err;
    }
  }, [cleanup, sendAudioForTranscription]);

  const stopAndTranscribe = useCallback((): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') {
        reject(new Error('Aucun enregistrement en cours'));
        return;
      }

      resolveStopRef.current = resolve;
      rejectStopRef.current = reject;
      setIsRecording(false);
      mediaRecorderRef.current.stop();
    });
  }, []);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.onstop = () => {
        cleanup();
        resolveStopRef.current = null;
        rejectStopRef.current = null;
      };
      setIsRecording(false);
      mediaRecorderRef.current.stop();
    }
  }, [cleanup]);

  // Annuler la dictée avec Échap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isRecording) {
        cancelRecording();
      }
    };

    if (isRecording) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isRecording, cancelRecording]);

  return {
    isRecording,
    isTranscribing,
    error,
    volumeData,
    startRecording,
    stopAndTranscribe,
    cancelRecording,
  };
};
